import React, { useState, useEffect } from 'react'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import KeyboardDoubleArrowLeft from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRight from '@mui/icons-material/KeyboardDoubleArrowRight'
import { useAuth } from "./api/authStore"
import moment from 'moment'
import dayjs from 'dayjs'
import ConfirmDialog from './ConfirmDialog'
import useMobile from './hooks/useMobile'
import MobileHistoryTable from './components/MobileHistoryTable'
import { HistoryDesktopDateRangePicker } from './HistoryDateRangePickers'

// Компонент пагинации
const PaginationActions = ({ page, rowsPerPage, count, onPageChange }) => {
    const safePage = isNaN(page) ? 0 : page
    const safeRowsPerPage = isNaN(rowsPerPage) ? 10 : rowsPerPage
    const safeCount = isNaN(count) ? 0 : count
    
    const totalPages = safeRowsPerPage === 0 ? 0 : Math.ceil(safeCount / safeRowsPerPage)
    
    const handleFirstPage = () => onPageChange(null, 0)
    const handlePrevPage = () => onPageChange(null, Math.max(0, safePage - 1))
    const handleNextPage = () => onPageChange(null, Math.min(totalPages - 1, safePage + 1))
    const handleLastPage = () => onPageChange(null, Math.max(0, totalPages - 1))
    
    return (
        <div style={{ display: 'flex', gap: '4px' }}>
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: safePage === 0 ? 'not-allowed' : 'pointer',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: safePage === 0 ? 0.5 : 1
                }}
                onClick={handleFirstPage}
                disabled={safePage === 0}
            >
                <KeyboardDoubleArrowLeft style={{ fontSize: '14px' }} />
            </button>
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: safePage === 0 ? 'not-allowed' : 'pointer',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: safePage === 0 ? 0.5 : 1
                }}
                onClick={handlePrevPage}
                disabled={safePage === 0}
            >
                <KeyboardArrowLeft style={{ fontSize: '14px' }} />
            </button>
            <span style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--text-muted)',
                margin: '0 var(--spacing-sm)',
                whiteSpace: 'nowrap',
                minWidth: '80px',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center'
            }}>
                {safePage + 1} of {totalPages}
            </span>
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: safePage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: safePage >= totalPages - 1 ? 0.5 : 1
                }}
                onClick={handleNextPage}
                disabled={safePage >= totalPages - 1}
            >
                <KeyboardArrowRight style={{ fontSize: '14px' }} />
            </button>
            <button 
                style={{
                    background: 'var(--dark-gray)',
                    border: '1px solid var(--light-gray)',
                    color: 'var(--text-light)',
                    padding: '8px',
                    borderRadius: 'var(--radius-sm)',
                    cursor: safePage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                    fontSize: 'var(--text-sm)',
                    minWidth: '36px',
                    height: '36px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: safePage >= totalPages - 1 ? 0.5 : 1
                }}
                onClick={handleLastPage}
                disabled={safePage >= totalPages - 1}
            >
                <KeyboardDoubleArrowRight style={{ fontSize: '14px' }} />
            </button>
        </div>
    )
}

// Компонент Snackbar для уведомлений
const CustomSnackbar = ({ open, onClose, message, severity = 'warning' }) => {
    if (!open) return null

    const getSeverityMeta = (severity) => {
        switch (severity) {
            case 'error':
                return { accent: '#E5484D', icon: '✕' }
            case 'success':
                return { accent: '#2EA043', icon: '✓' }
            case 'warning':
            default:
                return { accent: 'var(--primary-orange)', icon: '!' }
        }
    }

    const { accent, icon } = getSeverityMeta(severity)

    return (
        <div style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            background: 'var(--medium-gray)',
            color: 'var(--text-light)',
            border: '1px solid var(--light-gray)',
            borderLeft: `4px solid ${accent}`,
            padding: 'var(--spacing-md) var(--spacing-lg)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-card)',
            minWidth: '300px',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-md)',
            animation: 'fadeInDown 0.3s ease-out'
        }}>
            <span style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: accent,
                color: '#fff',
                fontSize: '14px',
                fontWeight: '700',
                flexShrink: 0
            }}>
                {icon}
            </span>
            <span style={{ flex: 1, fontSize: 'var(--text-sm)', fontWeight: '500' }}>{message}</span>
            <button
                onClick={onClose}
                style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-muted)',
                    cursor: 'pointer',
                    fontSize: 'var(--text-lg)',
                    flexShrink: 0
                }}
            >
                ✕
            </button>
            <style>{`
                @keyframes fadeInDown {
                    from {
                        opacity: 0;
                        transform: translateX(-50%) translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(-50%) translateY(0);
                    }
                }
            `}</style>
        </div>
    )
}

const HistoryTable = (props) => {
    const { getAccessTokenSilently } = useAuth()
    const { isMobile } = useMobile()

    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(10)
    const [historyList, setHistoyrList] = React.useState([])
    const [allHistoryData, setAllHistoryData] = React.useState([])
    const [startDate, setStartDate] = React.useState(() => dayjs().subtract(7, 'day'))
    const [endDate, setEndDate] = React.useState(() => dayjs())
    const [ipFilter, setIpFilter] = React.useState('')
    const [noteFilter, setNoteFilter] = React.useState('')

    // CSS стили для толстой полосы прокрутки и анимации спиннера
    React.useEffect(() => {
        const style = document.createElement('style')
        style.textContent = `
            /* Адаптивная полоса прокрутки для таблицы HistoryTable */
            .history-table-container::-webkit-scrollbar {
                height: 24px; /* В 3 раза толще стандартной */
            }
            
            .history-table-container::-webkit-scrollbar-track {
                background: var(--dark-gray);
                border-radius: 12px;
            }
            
            /* Светлая тема - оранжевая полоса */
            [data-theme="light"] .history-table-container::-webkit-scrollbar-thumb {
                background: var(--primary-orange);
                border-radius: 12px;
                border: 3px solid var(--dark-gray);
            }
            
            [data-theme="light"] .history-table-container::-webkit-scrollbar-thumb:hover {
                background: var(--secondary-orange);
            }
            
            /* Темная тема - светлая серая полоса */
            [data-theme="dark"] .history-table-container::-webkit-scrollbar-thumb,
            :root:not([data-theme="light"]) .history-table-container::-webkit-scrollbar-thumb {
                background: var(--light-gray);
                border-radius: 12px;
                border: 3px solid var(--dark-gray);
            }
            
            [data-theme="dark"] .history-table-container::-webkit-scrollbar-thumb:hover,
            :root:not([data-theme="light"]) .history-table-container::-webkit-scrollbar-thumb:hover {
                background: #B0B0B0;
            }
            
            /* Для Firefox - светлая тема */
            [data-theme="light"] .history-table-container {
                scrollbar-width: thick;
                scrollbar-color: var(--primary-orange) var(--dark-gray);
            }
            
            /* Для Firefox - темная тема */
            [data-theme="dark"] .history-table-container,
            :root:not([data-theme="light"]) .history-table-container {
                scrollbar-width: thick;
                scrollbar-color: var(--light-gray) var(--dark-gray);
            }
            
            /* Анимация спиннера */
            @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
            }
        `
        document.head.appendChild(style)
        
        return () => {
            document.head.removeChild(style)
        }
    }, [])


    //history purchase
    const [confirmOpen, setConfirmOpen] = React.useState(false)
    const [selectedRow, setSelectedRow] = useState([])
    const [afterBuy, setAfterBuy] = React.useState(false)
    const [errorGetAgentInfo, setErrorGetAgentInfo] = useState(false)
    const [loadingRowId, setLoadingRowId] = useState(null)
    
    //notes
    const [notes, setNotes] = useState({})


    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - historyList?.expiredRentals?.length) : 0

    const handleChangePage = (event, newPage) => {
        const validPage = isNaN(newPage) ? 0 : Math.max(0, newPage)
        setPage(validPage)
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    // Загрузка данных с сервера
    useEffect(()=>{
      const callAsync = async() => {
          try {
            const dateStartFormated = startDate.format('YYYY-MM-DD')
            const dateEndFormated = endDate.format('YYYY-MM-DD')
            
            // Получаем токен один раз
            const token = await getAccessTokenSilently()
            
            const historyPesponse = await props.apiClient.setToken(token).getUserHistory(dateStartFormated, dateEndFormated, 0, 300) // Загружаем все данные
            // console.log(historyPesponse.items)
            setAllHistoryData(historyPesponse)
          } catch (e) {
              // Игнорируем ошибки отмены запросов
              if (e.name !== 'AbortError' && e.code !== 'ECONNABORTED') {
                  console.error('HistoryTable error:', e)
              }
          }
          
      }
      callAsync()   
    },[startDate, endDate, getAccessTokenSilently, props.apiClient])

    // Функция для фильтрации данных по IP и запискам
    const filterHistoryByIpAndNotes = React.useCallback((data, ipFilter, noteFilter, notesData) => {
      if (!data?.expiredRentals) {
        return data
      }
      
      let filteredItems = data.expiredRentals
      
      // Фильтрация по IP
      if (ipFilter && ipFilter.trim() !== '') {
        filteredItems = filteredItems.filter(item => 
          item.ip && item.ip.toLowerCase().includes(ipFilter.toLowerCase())
        )
      }
      
      // Фильтрация по запискам
      if (noteFilter && noteFilter.trim() !== '') {
        filteredItems = filteredItems.filter(item => {
          const note = notesData[item.proxyid] || item.note || ''
          return note.toLowerCase().includes(noteFilter.toLowerCase())
        })
      }
      
      return {
        ...data,
        expiredRentals: filteredItems,
        total: data.total,
        filteredTotal: filteredItems.length
      }
    }, [])

    // Применение фильтрации по IP и запискам
    useEffect(() => {
      const filteredData = filterHistoryByIpAndNotes(allHistoryData, ipFilter, noteFilter, notes)
      setHistoyrList(filteredData)
    }, [allHistoryData, ipFilter, noteFilter, notes, filterHistoryByIpAndNotes])

    const handleDateRangeChange = React.useCallback((start, end) => {
      setStartDate(start)
      setEndDate(end)
    }, [])

    const handleIpFilterChange = React.useCallback((event) => {
      setIpFilter(event.target.value)
      setPage(0) // Сброс на первую страницу при изменении фильтра
    }, [])

    const handleNoteFilterChange = React.useCallback((event) => {
      setNoteFilter(event.target.value)
      setPage(0) // Сброс на первую страницу при изменении фильтра
    }, [])

    const handleButtonClick = React.useCallback(async(row) => {
       try {
          setLoadingRowId(row?.proxyid)
          const agentData = await props.apiClient.setToken(getAccessTokenSilently()).getAgentInfo(row?.proxyid)
          if(agentData?.proxyid == null || agentData?.proxyid == undefined){
              throw 'error'
          }

          setSelectedRow(agentData)
          setConfirmOpen(true)
       } catch(e){
          // console.log(agentData)
          setErrorGetAgentInfo(true)
       } finally {
          setLoadingRowId(null)
       }
    }, [])

    const handleCloseGetAgentInfoError = React.useCallback(() => {
      setErrorGetAgentInfo(false)
    }, [])

    useEffect(() => {
      if (errorGetAgentInfo) {
        const timer = setTimeout(() => setErrorGetAgentInfo(false), 4000)
        return () => clearTimeout(timer)
      }
    }, [errorGetAgentInfo])


    // Если мобильная версия, используем мобильный компонент
    if (isMobile) {
        return <MobileHistoryTable {...props} />
    }

    return (
        <>
            <div>
                <h1 style={{ 
                    margin: '0 0 var(--spacing-lg) 0',
                    fontWeight: 600,
                    fontSize: 'var(--text-2xl)',
                    background: 'var(--gradient-orange)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                }}>
                    History
                </h1>
                
                {/* Фильтры по дате и IP */}
                <div style={{
                    background: 'var(--medium-gray)',
                    border: '1px solid var(--light-gray)',
                    borderRadius: 'var(--radius-lg)',
                    padding: 'var(--spacing-lg)',
                    paddingBottom: 'calc(var(--spacing-lg) - 37px)',
                    marginBottom: 'calc(var(--spacing-lg) - 10px)',
                    boxShadow: 'var(--shadow-card)',
                    display: 'flex',
                    gap: 'var(--spacing-lg)',
                    alignItems: 'end'
                }}>
                    <HistoryDesktopDateRangePicker
                        label="Date range"
                        startDate={startDate}
                        endDate={endDate}
                        onRangeChange={handleDateRangeChange}
                    />
                    <div className="form-group" style={{ width: '200px' }}>
                        <label className="form-label">
                            Search by IP
                            {ipFilter && (
                                <span style={{
                                    marginLeft: '8px',
                                    fontSize: 'var(--text-xs)',
                                    color: 'var(--primary-orange)',
                                    fontWeight: '500'
                                }}>
                                    ({historyList?.total || 0} found)
                                </span>
                            )}
                        </label>
                        <input
                            type="text"
                            className={`form-input ${ipFilter ? 'active' : ''}`}
                            placeholder="Enter IP address..."
                            value={ipFilter}
                            onChange={handleIpFilterChange}
                            style={{
                                color: 'var(--text-light)',
                                width: '100%',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>
                    <div className="form-group" style={{ width: '200px' }}>
                        <label className="form-label">
                            Search by Notes
                            {noteFilter && (
                                <span style={{
                                    marginLeft: '8px',
                                    fontSize: 'var(--text-xs)',
                                    color: 'var(--primary-orange)',
                                    fontWeight: '500'
                                }}>
                                    ({historyList?.total || 0} found)
                                </span>
                            )}
                        </label>
                        <input
                            type="text"
                            className={`form-input ${noteFilter ? 'active' : ''}`}
                            placeholder="Enter note text..."
                            value={noteFilter}
                            onChange={handleNoteFilterChange}
                            style={{
                                color: 'var(--text-light)',
                                width: '100%',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>
                </div>
                
                {historyList?.expiredRentals?.length > 0 ? (
                    <div className="table-container history-table-container" style={{ overflowX: 'auto' }}>
                        <table className="table" style={{ minWidth: '1000px' }}>
                            <thead>
                                <tr>
                                    <th>IP</th>
                                    <th>Country</th>
                                    <th>State</th>
                                    <th>City</th>
                                    <th>Zip</th>
                                    <th>ISP</th>
                                    <th>Date</th>
                                    <th>Note</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    // Всегда показываем элементы текущей страницы (локальная пагинация)
                                    const startIndex = page * rowsPerPage
                                    const endIndex = startIndex + rowsPerPage
                                    return historyList?.expiredRentals?.slice(startIndex, endIndex)?.map((row, index) => (
                                    <tr key={index}>
                                        <td>{row?.ip}</td>
                                        <td>{row?.geo?.cc}</td>
                                        <td>{row?.geo?.reg}</td>
                                        <td>{row?.geo?.city}</td>
                                        <td>{row?.geo?.zip}</td>
                                        <td>{row?.geo?.isp?.replace(/(.{10})..+/, "$1…")}</td>
                                        <td>{moment(row.expiredAt).format('MMM-D LT')}</td>
                                        <td style={{ maxWidth: '200px', wordWrap: 'break-word' }}>
                                            <span 
                                                style={{
                                                    fontSize: 'var(--text-sm)',
                                                    color: notes[row?.proxyid] || row?.note ? 'var(--text-light)' : 'var(--text-muted)',
                                                    fontStyle: notes[row?.proxyid] || row?.note ? 'normal' : 'italic',
                                                    cursor: (notes[row?.proxyid] || row?.note) ? 'help' : 'default'
                                                }}
                                                title={(notes[row?.proxyid] || row?.note || 'No note').replace(/(.{20})/g, '$1\n')}
                                            >
                                                {(notes[row?.proxyid] || row?.note || 'No note').length > 10 
                                                    ? (notes[row?.proxyid] || row?.note || 'No note').substring(0, 10) + '...'
                                                    : (notes[row?.proxyid] || row?.note || 'No note')
                                                }
                                            </span>
                                        </td>
                                        <td>
                                            <button 
                                                className="btn btn-buy btn-small"
                                                onClick={() => handleButtonClick(row)}
                                                disabled={loadingRowId === row?.proxyid}
                                                style={{ 
                                                    minWidth: '80px',
                                                    opacity: loadingRowId === row?.proxyid ? 0.7 : 1,
                                                    cursor: loadingRowId === row?.proxyid ? 'not-allowed' : 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px'
                                                }}
                                            >
                                                {loadingRowId === row?.proxyid ? (
                                                    <div style={{
                                                        width: '16px',
                                                        height: '16px',
                                                        border: '2px solid var(--light-gray)',
                                                        borderTop: '2px solid var(--primary-orange)',
                                                        borderRadius: '50%',
                                                        animation: 'spin 1s linear infinite'
                                                    }} />
                                                ) : (
                                                    'Buy'
                                                )}
                                            </button>
                                        </td>
                                    </tr>
                                    ))
                                })()}
                            </tbody>
                        </table>
                        
                        {/* Информация о фильтрации */}
                        {(ipFilter || noteFilter) && (
                            <div style={{
                                padding: 'var(--spacing-md)',
                                background: 'var(--dark-gray)',
                                borderTop: '1px solid var(--light-gray)',
                                borderBottom: '1px solid var(--light-gray)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                                    <span style={{ color: 'var(--primary-orange)', fontSize: 'var(--text-sm)' }}>
                                        🔍 Filtered results: {historyList?.filteredTotal || 0} of {historyList?.total || 0} records
                                    </span>
                                    {(ipFilter || noteFilter) && (
                                        <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)' }}>
                                            (Clear filters to use pagination)
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                        
                        {/* Пагинация */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: 'var(--spacing-lg)',
                            borderTop: '1px solid var(--light-gray)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                                <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                                    Rows per page:
                                </span>
                                <select 
                                    value={rowsPerPage}
                                    onChange={handleChangeRowsPerPage}
                                    style={{
                                        background: 'var(--medium-gray)',
                                        border: '1px solid var(--light-gray)',
                                        borderRadius: 'var(--radius-sm)',
                                        color: 'var(--text-light)',
                                        padding: '8px 12px',
                                        fontSize: 'var(--text-sm)'
                                    }}
                                >
                                    {[10, 20, 30, 40, 50].map(option => (
                                        <option key={option} value={option}>{option}</option>
                                    ))}
                                </select>
                            </div>
                            
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
                            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
                                {isNaN(page) ? 0 : page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, historyList?.total || 0)} of {historyList?.total || 0}
                            </span>
                                <PaginationActions 
                                    page={page}
                                    rowsPerPage={rowsPerPage}
                                    count={historyList?.total || 0}
                                    onPageChange={handleChangePage}
                                />
                            </div>
                        </div>
                    </div>
                ) : (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 'var(--spacing-2xl)',
                        background: 'var(--medium-gray)',
                        border: '1px solid var(--light-gray)',
                        borderRadius: 'var(--radius-lg)',
                        boxShadow: 'var(--shadow-card)',
                        textAlign: 'center'
                    }}>
                        <div style={{
                            fontSize: '48px',
                            marginBottom: 'var(--spacing-lg)'
                        }}>
                            📋
                        </div>
                        <h3 style={{
                            color: 'var(--text-light)',
                            fontSize: 'var(--text-lg)',
                            fontWeight: '600',
                            margin: '0 0 var(--spacing-sm) 0'
                        }}>
                            No history records
                        </h3>
                        <p style={{
                            color: 'var(--text-muted)',
                            fontSize: 'var(--text-sm)',
                            margin: '0',
                            lineHeight: '1.5'
                        }}>
                            You don't have any history records for the selected period.
                        </p>
                    </div>
                )}
                
                <ConfirmDialog 
                    open={confirmOpen} 
                    setConfirmOpen={setConfirmOpen} 
                    selectedProxy={selectedRow} 
                    apiClient={props.apiClient} 
                    setAfterBuy={setAfterBuy}
                    refreshUserData={props.refreshUserData}
                />
                
                <CustomSnackbar
                    open={errorGetAgentInfo}
                    onClose={handleCloseGetAgentInfoError}
                    message="Couldn't get proxy info. Please try again later."
                    severity="warning"
                />
            </div>
        </>
    )
}

export default HistoryTable